import { computed, useHost, type ComputedRef } from "vue";
import { useSharedState } from "composables/useSharedState";
import { useComponentContext } from "composables/useComponentContext";
import { useStateStore } from "composables/useStateStore";
import { createEventDispatcher } from "@/runtime/shared/events";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import type { AccordionItemProps } from "./accordion.types";
import {
  accordionSummaryClasses,
  accordionDetailsClasses,
  accordionIndicatorClasses,
  accordionContentClasses,
} from "./accordion.styles";
import { getAccordionToggle } from "./useAccordion";
import { cardClasses } from "components/Card/card.styles";

export interface UseAccordionItemOptions {
  props: Readonly<AccordionItemProps>;
}

interface ClassDeps {
  variant: ComputedRef<string | undefined>;
  size: ComputedRef<string | undefined>;
  disabled: ComputedRef<boolean | undefined>;
  isOpen: ComputedRef<boolean>;
  propsClass: string | undefined;
}

const buildClasses = (deps: ClassDeps) => {
  return {
    card: computed(() => {
      const v = deps.variant.value;
      const isTransp = !v || v === "transparent";
      let base = "p-0! overflow-hidden";
      if (isTransp && deps.isOpen.value) {
        base += " bg-(--color-elevation-200)/10!";
      }
      return mergeClassProp(base, deps.propsClass);
    }),
    summary: computed(() => {
      const v = deps.variant.value;
      const s = deps.size.value;
      const summaryProps: Record<string, string | boolean> = {
        disabled: !!deps.disabled.value,
        open: deps.isOpen.value,
      };
      if (s) summaryProps.size = s;
      if (v) summaryProps.variant = v;
      return accordionSummaryClasses(summaryProps);
    }),
    details: computed(() => accordionDetailsClasses({})),
    indicator: computed(() => accordionIndicatorClasses({ open: deps.isOpen.value })),
    content: computed(() => {
      const v = deps.variant.value;
      const s = deps.size.value;
      const contentProps: Record<string, string> = {};
      if (s) contentProps.size = s;
      if (v === "transparent") contentProps.variant = "transparent";
      return accordionContentClasses(contentProps);
    }),
  };
};

export const useAccordionItem = (options: UseAccordionItemOptions) => {
  const { props } = options;
  const host = useHost();
  const state = useSharedState(props);
  const context = useComponentContext<AccordionItemProps>();
  const stateStore = useStateStore<AccordionItemProps>();

  const dispatchEvent = createEventDispatcher(host);

  const parentAccordion = computed(() => {
    if (context.uid == null) return;
    return stateStore.findParentOfType(context.uid, "Accordion");
  });

  const isOpen = computed(() => !!state.open?.value);

  const onSummaryClick = (event: Event) => {
    event.preventDefault();
    if (state.disabled?.value) return;
    const newOpen = !isOpen.value;
    const parentUid = parentAccordion.value?.uid;
    if (parentUid != null) {
      getAccordionToggle(parentUid)?.(context.uid!, newOpen);
    }
    dispatchEvent("update:open", newOpen);
  };

  const variant = computed(() => state.variant?.value);
  const size = computed(() => state.size?.value);
  const color = computed(() => state.color?.value);
  const disabled = computed(() => state.disabled?.value);

  // Map accordion variant to card variant. transparent → ghost gives a transparent bg
  // with the right text color; no chrome / backdrop / ring.
  const cardVariant = computed(() => {
    const v = variant.value;
    if (!v || v === "transparent") return "ghost" as const;
    return v as "solid" | "outline" | "soft" | "subtle" | "ghost" | "link";
  });

  const classes = buildClasses({ variant, size, disabled, isOpen, propsClass: props.class });

  const innerCls = computed(() => {
    const card = cardClasses({
      color: color.value ?? "neutral",
      variant: cardVariant.value,
      size: size.value ?? "sm",
    });
    return `${card} ${classes.card.value}`;
  });

  return { state, onSummaryClick, isOpen, classes, color, cardVariant, size, innerCls };
};

export const useAccordionSlot = (slotName: string) => {
  const host = useHost();
  if (host) host.slot = slotName;
};
